import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Section from './Section';
import Button from './Button';
import { useSanityContent } from '../hooks/useSanityContent';
import { fetchProductsBento } from '../lib/sanity';
import { defaultProductsBento } from '../lib/defaultContent';
import type { ProductsBentoContent } from '../lib/types';

interface CardProps {
  name: string;
  tag: string;
  image: string;
  className?: string;
  index: number;
  radius?: string;
}

const ProductCard: React.FC<CardProps> = ({
  name,
  tag,
  image,
  className = '',
  index,
  radius = 'rounded-xl',
}) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.96 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.45, delay: index * 0.06 }}
    className={`relative overflow-hidden group ${radius} ${className}`}
  >
    <Link to="/products" className="block w-full h-full">
      <img
        src={image}
        alt={name}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
      <div className="absolute bottom-0 left-0 p-3 sm:p-4">
        <span className="inline-block px-2 py-0.5 bg-primary-red text-white text-[10px] font-inter font-bold rounded-full mb-1.5 uppercase tracking-wider">
          {tag}
        </span>
        <h3 className="text-white font-playfair font-bold text-base sm:text-lg leading-tight">
          {name}
        </h3>
      </div>
    </Link>
  </motion.div>
);

const ProductsBento: React.FC = () => {
  const { content } = useSanityContent<ProductsBentoContent>(
    fetchProductsBento,
    defaultProductsBento
  );
  const products = content.products && content.products.length
    ? content.products
    : defaultProductsBento.products;
  return (
    <Section padding="default">
      {/* Heading + CTA inline */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6"
      >
        <div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-playfair font-bold text-primary-black mb-1.5">
            {content.title}
          </h2>
          <p className="text-sm sm:text-base text-gray-600 max-w-lg">
            {content.description}
          </p>
        </div>
        <div className="flex-shrink-0">
          <Button variant="primary" href={content.ctaLink}>
            {content.ctaText}
          </Button>
        </div>
      </motion.div>

      {/* ── Desktop scattered grid (4-col, 2-row) ── */}
      {/*
          Col:  1        2        3        4
          Row1: Rice     Maize──────────   Sugar
          Row2: Rice     Beans    Groundnuts──────
      */}
      <div
        className="hidden lg:grid grid-cols-4 gap-3"
        style={{ gridTemplateRows: '190px 190px' }}
      >
        {/* Rice — tall portrait (col 1, rows 1-2) */}
        <ProductCard
          {...products[0]}
          index={0}
          className="row-span-2"
          radius="rounded-2xl"
        />
        {/* Maize — wide (cols 2-3, row 1) */}
        <ProductCard
          {...products[1]}
          index={1}
          className="col-span-2"
          radius="rounded-xl"
        />
        {/* Sugar — small square (col 4, row 1) */}
        <ProductCard
          {...products[2]}
          index={2}
          radius="rounded-lg"
        />
        {/* Beans — small (col 2, row 2) */}
        <ProductCard
          {...products[3]}
          index={3}
          radius="rounded-lg"
        />
        {/* Groundnuts — wide (cols 3-4, row 2) */}
        <ProductCard
          {...products[4]}
          index={4}
          className="col-span-2"
          radius="rounded-xl"
        />
      </div>

      {/* ── Mobile grid (2-col) ── */}
      <div className="grid grid-cols-2 gap-2.5 lg:hidden">
        {products.map((p, i) => (
          <ProductCard
            key={p.name}
            {...p}
            index={i}
            className={`h-[150px] ${i === 0 ? 'col-span-2 h-[180px]' : ''}`}
          />
        ))}
      </div>
    </Section>
  );
};

export default ProductsBento;
